import { Ulid } from "@kase/my-marathon-communication";
import { effect, signal } from "@preact/signals";
import { local } from "~/core/local";
import { CalendarData, MakeSettingsParams } from "./interfaces";

const CALENDARS_KEY = "settings/selected-calendars";

// @TODO: move some of the fields into separate slice
// ex. projectGroups, showInactive as they rather search filters and they are not on the profile page
//
const localSettings = local(
    "v1.1_settings",
    {
        projectGroups: [] as string[],
        showInactiveProjects: false,
        showInactiveActivities: false,
        showInactiveFeeCategories: false,
        showFeecodeDeviation: true,
        showEmployeeFeecodeDeviation: true,
        marqueeEnabled: false,
        dashboardStartup: true,
        selectedTypes: null as string[] | null,
        selectedStatus: null as string | null,
    },
    (value, defaultValue) => {
        if (!Array.isArray(value.projectGroups)) {
            value.projectGroups = defaultValue.projectGroups;
        }

        if (typeof value.showFeecodeDeviation !== "boolean") {
            value.showFeecodeDeviation = defaultValue.showFeecodeDeviation;
        }

        if (typeof value.showEmployeeFeecodeDeviation !== "boolean") {
            value.showEmployeeFeecodeDeviation =
                defaultValue.showEmployeeFeecodeDeviation;
        }

        if (typeof value.marqueeEnabled !== "boolean") {
            value.marqueeEnabled = defaultValue.marqueeEnabled;
        }

        if (typeof value.dashboardStartup !== "boolean") {
            value.dashboardStartup = defaultValue.dashboardStartup;
        }

        return value;
    },
);

const makeSettings = ({ api }: MakeSettingsParams) => {
    const projectGroups = signal(localSettings.get().projectGroups);
    const showInactiveProjects = signal(false);
    const showInactiveActivities = signal(
        localSettings.get().showInactiveActivities,
    );
    const showInactiveFeeCategories = signal(
        localSettings.get().showInactiveFeeCategories,
    );
    const showFeecodeDeviation = signal(
        localSettings.get().showFeecodeDeviation,
    );
    const showEmployeeFeecodeDeviation = signal(
        localSettings.get().showEmployeeFeecodeDeviation,
    );
    const marqueeEnabled = signal(localSettings.get().marqueeEnabled);
    const dashboardStartup = signal(localSettings.get().dashboardStartup);
    const selectedCalendars = signal<Record<string, string[]>>({});
    const selectedTypes = signal(localSettings.get().selectedTypes);
    const selectedStatus = signal(localSettings.get().selectedStatus);
    const allSelectedState: Record<string, boolean> = {};

    effect(() => {
        localSettings.set({
            projectGroups: projectGroups.value,
            showInactiveProjects: showInactiveProjects.value,
            showInactiveActivities: showInactiveActivities.value,
            showInactiveFeeCategories: showInactiveFeeCategories.value,
            showFeecodeDeviation: showFeecodeDeviation.value,
            showEmployeeFeecodeDeviation: showEmployeeFeecodeDeviation.value,
            marqueeEnabled: marqueeEnabled.value,
            dashboardStartup: dashboardStartup.value,
            selectedTypes: selectedTypes.value,
            selectedStatus: selectedStatus.value,
        });
    });

    const getActive = (service: string) =>
        api.account.calendarList.items
            .peek()
            .filter((i) => i.active && i.service === service)
            .map((i) => i.calendar_id);

    const idsEqual = (a: string[], b: string[]) => {
        if (a.length !== b.length) {
            return false;
        }

        return a.toSorted().join("#") === b.toSorted().join("#");
    };

    const persist = (key: string, value: unknown) => {
        api.clientStorage.set(
            key,
            typeof value === "string" ? value : JSON.stringify(value),
        );
    };

    const toggleCalendar = (
        service: string,
        id: Readonly<Ulid>,
        calendarId: string,
        checked: boolean,
    ) => {
        return api.account.toggleCalendar(id, checked);
    };

    const selectCalendars = (service: string, ids: string[]) => {
        const newValue = {
            ...selectedCalendars.peek(),
            [service]: ids,
        };

        allSelectedState[service] = idsEqual(ids, getActive(service));

        persist(CALENDARS_KEY, newValue);
        selectedCalendars.value = newValue;
    };

    const syncCalendars = (service: string, calendars: CalendarData[]) => {
        const selected = selectedCalendars.peek();
        let newSelected: string[] = [];

        if (service in selected) {
            // if configuration for this service already exists, remove any calendars that are no longer available
            newSelected = selected[service].filter((calendarId) =>
                calendars.some((calendar) => calendar.id === calendarId),
            );

            if (newSelected.length === 0 || allSelectedState[service]) {
                newSelected = calendars.map((calendar) => calendar.id);
            }
        } else {
            // otherwise it's a first load, make default calendars active
            newSelected = getActive(service);
        }

        const newValue = {
            ...selected,
            [service]: newSelected,
        };

        allSelectedState[service] = idsEqual(
            newSelected,
            calendars.map((i) => i.id),
        );

        if (JSON.stringify(newValue) === JSON.stringify(selected)) {
            // do no update if nothing changed
            return;
        }

        persist(CALENDARS_KEY, newValue);
        selectedCalendars.value = newValue;
    };

    const setup = async () => {
        try {
            const sCalendars = await api.clientStorage.get(CALENDARS_KEY);

            if (typeof sCalendars === "string") {
                selectedCalendars.value = JSON.parse(sCalendars);
            }
        } catch (err: unknown) {
            console.error(err);
        }
    };

    return {
        projectGroups,
        showInactiveProjects,
        showInactiveActivities,
        showInactiveFeeCategories,
        showFeecodeDeviation,
        showEmployeeFeecodeDeviation,
        marqueeEnabled,
        dashboardStartup,
        selectedCalendars,
        selectedTypes,
        selectedStatus,
        persist,
        setup,
        toggleCalendar,
        syncCalendars,
        selectCalendars,
    };
};

export default makeSettings;
